import { useEffect, useMemo, useRef, useState } from "react"

import { useNavigate } from "react-router-dom"

import { AIModel } from "@llumiverse/core"
import { AsyncExecutionResult } from "@vertesia/client"
import { AgentMessage, AgentMessageType, AsyncConversationExecutionPayload, CollectionItem, ContentObjectItem, ExecutionRun, ExecutionRunRef, Interaction, InteractionRef, UserInputSignal, WorkflowRunWithDetails } from "@vertesia/common"
import { JSONSchema4Object } from "json-schema"
import { ArrowUp, ChevronsDown, ChevronsUp } from "lucide-react"
import { toast } from "sonner"

import { AgentAccessModeBadge } from "@components/badges/AgentAccessModeBadge"
import { AgentModeBadge } from "@components/badges/AgentModeBadge"
import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"
import { FileSelectedButton } from "@components/buttons/FileSelectedButton"
import { AllMessagesMixed } from "@components/panels/agent-runner/AllMessagesMixed"
import { FilesSelectionPanel } from "@components/panels/agent-runner/FilesSelectionPanel"
import { Header } from "@components/panels/agent-runner/Header"
import { PlanPanel } from "@components/panels/agent-runner/PlanPanel"
import { LatestUpdateMessagePanel } from "@components/panels/LatestUpdateMessagePanel"
import { ResizablePanel } from "@components/panels/ResizablePanel"
import { AgentPanel } from "@components/panels/services/AgentPanel"
import { AvailableServicesPanel } from "@components/panels/services/AvailableServicesPanel"
import { ServicePanel } from "@components/panels/services/ServicePanel"
import { AgentModelSelection } from "@components/selections/AgentModelSelection"

import { initialMessage, initialDetailedMessage, generateChatMessage, generateMessage } from "@constants/agents"
import { AGENT_MODE_STATE } from "@constants/cookies"

import { useTabNotification } from "@hooks/use-tab-notification"

import { insertMessageInTimeline, isInProgress } from "@lib/agent"
import { extractCollectionId, extractCollectionIdFromExecution } from "@lib/workflow"

import { Plan } from "@models/agents"
import { ChatMessageRoleType, FileMessage, Message } from "@models/messages"

import { usePayloadBuilder } from "@plugins/agent-runner/PayloadBuilder"
import { useWatchSearchResult } from "@plugins/providers/agent/AvailableAgentContext"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Textarea } from "@ui/textarea"

export function AgentChatPanel() {
    const { updateDocumentTitle } = useTabNotification()
    const { search, agent, defaultAgent, chat } = useWatchSearchResult()

    const navigate = useNavigate()

    const { client, project, user } = useUserSession()
    const username = user?.name.split(" ")[0] || "User"

    const builder = usePayloadBuilder()

    const [browseInteraction, setBrowseInteraction] = useState(false)
    const [searchInteraction, setSearchInteraction] = useState(true)
    const [agentInteraction, setAgentInteraction] = useState(false)
    const [interactionRef, setInteractionRef] = useState<InteractionRef | undefined>(undefined)

    const bottomRef = useRef<HTMLDivElement | null>(null)
    const [isSending, setIsSending] = useState(false)

    const [collection, setCollection] = useState<CollectionItem | undefined>(undefined)

    const [run, setRun] = useState<AsyncExecutionResult | undefined>(decodeWorkflowRun(window.location.hash))
    const [runDetails, setRunDetails] = useState<WorkflowRunWithDetails | undefined>(undefined)
    const [chatId, setChatId] = useState<string | undefined>(decodeChatId(window.location.hash))
    const [loadData, setLoadData] = useState(true)
    const [loadServiceData, setLoadServiceData] = useState<ExecutionRun | undefined>(undefined)

    const [interaction, setInteraction] = useState<Interaction | undefined>()
    const [model, setModel] = useState<string | undefined>(undefined)
    const [currentModel, setCurrentModel] = useState<AIModel | undefined>(undefined)
    const [loadModel, setLoadModel] = useState(true)
    const [files, setFiles] = useState<ContentObjectItem[]>([])

    const textareaRef = useRef<HTMLTextAreaElement | null>(null)
    const [inputMessage, setInputMessage] = useState("")
    const [messages, setMessages] = useState<AgentMessage[]>([])
    const [chatMessages, setChatMessages] = useState<Message[]>([])

    const [plans, setPlans] = useState<Array<{ plan: Plan; timestamp: number }>>([])
    const [activePlanIndex, setActivePlanIndex] = useState<number>(0)
    const [workstreamStatusMap, setWorkstreamStatusMap] = useState<Map<number, Map<string, "pending" | "in_progress" | "completed" | "skipped">>>(new Map())

    const [createdFiles, setCreatedFiles] = useState<string[]>([])
    const [fileMessages, setFileMessages] = useState<FileMessage[]>([])
    const [filesToPass, setFilesToPass] = useState<string[]>([])

    const [viewMode, setViewMode] = useState<"agent" | "stacked">("agent")
    const [isCompleted, setIsCompleted] = useState(true)

    const [agentMode, setAgentMode] = useState<boolean>(localStorage.getItem(AGENT_MODE_STATE) !== "basic")
    const [agentModeValue, setAgentModeValue] = useState<string | undefined>(undefined)
    const [accessType, setAccessType] = useState<"private" | "shared">("private")

    const onAgentModeChange = (value: boolean) => {
        localStorage.setItem(AGENT_MODE_STATE, value ? "reasoning" : "basic")
        setAgentMode(value)
        if (value) {
            search.setAgent(defaultAgent)
        } else {
            setAccessType("private")
            search.setAgent(chat)
        }
    }

    const onAccessTypeChange = (value: boolean) => {
        setAccessType(value ? "shared" : "private")
    }

    const insertNewLineAtCursor = () => {
        const textarea = textareaRef.current
        if (!textarea) {
            return
        }

        const cursorPosition = textarea.selectionStart

        const beforeCursor = inputMessage.substring(0, cursorPosition)
        const afterCursor = inputMessage.substring(cursorPosition)

        const newValue = beforeCursor + "\n" + afterCursor
        setInputMessage(newValue)

        setTimeout(() => {
            textarea.focus()
            textarea.setSelectionRange(cursorPosition + 1, cursorPosition + 1)
        }, 0)
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            if (e.metaKey || e.altKey) {
                e.preventDefault()
                handleSendMessage()
            } else {
                insertNewLineAtCursor()
            }
        }
    }

    const handleFilesUpdate = (newFiles: ContentObjectItem[]) => {
        setFiles((prev) => [...prev, ...newFiles])
    }

    const handleFilesRemove = (fileToRemove: ContentObjectItem) => {
        setFiles((prev) => prev.filter((file) => file.id !== fileToRemove.id))
    }

    const handleOnNameChange = (name: string) => {
        if (!collection || !name) {
            return
        }

        updateDocumentTitle(name)

        client.store.collections
            .get(collection.id)
            .then((tmpCollection) => {
                if (!tmpCollection) {
                    return
                }

                const payload = {
                    name: name,
                    properties: (tmpCollection as any).properties || {},
                }

                client.store.collections
                    .update(collection.id, payload)
                    .then(() => {
                        const newCollection = { ...tmpCollection, name }
                        setCollection(newCollection)
                    })
                    .catch((err) => {
                        console.warn(`Failed to update collection ${collection.id}`, err)
                    })
            })
            .catch((err) => {
                console.warn(`Failed to retrieve collection ${collection.id}`, err)
            })
    }

    const handleNewChat = () => {
        window.history.pushState(null, "", window.location.pathname)
        window.location.reload()
    }

    const handleOnShare = () => {
        if (!project) {
            return
        }

        let url = undefined
        if (run && run.runId && run.workflowId) {
            url = `${new URL(window.location.origin).toString()}assistant?a=${project.account}&p=${project.id}#${run.runId}__${run.workflowId}`
        } else if (chatId) {
            url = `${new URL(window.location.origin).toString()}assistant?a=${project.account}&p=${project.id}#${chatId}`
        }

        if (!url) {
            return
        }

        navigator.clipboard.writeText(url)
        toast.success("Link copied", {
            description: `Link copied to clipboard`,
            duration: 2000,
        })
    }

    const handleCopyRunId = () => {
        if (!run) {
            return
        }

        navigator.clipboard.writeText(run.runId)
        toast.success("Run ID copied", {
            description: `Run ID ${run.runId} copied to clipboard`,
            duration: 2000,
        })
    }

    const handleCopyChatId = () => {
        if (!chatId) {
            return
        }

        navigator.clipboard.writeText(chatId)
        toast.success("Chat ID copied", {
            description: `Chat ID ${chatId} copied to clipboard`,
            duration: 2000,
        })
    }

    const handleSendMessage = () => {
        if (agentMode) {
            handleAgentMessage()
        } else {
            handleChatMessage()
        }
    }

    const handleOnPayloadReady = async (tmpInt: Interaction, accessType: "private" | "shared", data: JSONSchema4Object | undefined) => {
        if (!client || !tmpInt || !data) {
            return
        }

        for (const key in data) {
            if (Array.isArray(data[key]) && data[key].length === 0) {
                delete data[key]
            } else if (typeof data[key] === "string" && data[key].trim() === "") {
                delete data[key]
            }
        }

        setInteraction(tmpInt)

        setAccessType(accessType)
        setSearchInteraction(true)
        setBrowseInteraction(false)

        const taskName = generateTaskName(tmpInt)

        const collection = await client.store.collections.create({
            name: taskName,
            dynamic: false,
        })

        data.collection = collection.id

        const payload = {
            type: "conversation",
            data,
            interactive: true,
            interaction: tmpInt.endpoint,
            environment: tmpInt.environment,
            model: tmpInt.model,
            tools: [],
            tags: [accessType, `collection:${collection.id}`],
            visibility: accessType === "private" ? "private" : "project",
        } as AsyncConversationExecutionPayload

        client.interactions
            .executeAsync(payload)
            .then((run) => {
                setRun(run)

                client.store.collections.update(collection.id, {
                    properties: {
                        status: "in_progress",
                    },
                })

                location.hash = `#${run.runId + "__" + run.workflowId}`
                updateDocumentTitle(taskName)

                setMessages((prevMessages) => [...prevMessages, initialDetailedMessage(`Can you help with ${tmpInt.name}`, { ...builder.payload }, run.runId)])
                setInputMessage("")
                setFiles([])
                setIsCompleted(false)
                setAgentModeValue(accessType)
            })
            .catch((err) => {
                toast.error("Failed to start conversation", {
                    description: err.message,
                    duration: 5000,
                })
            })
    }

    const handleAgentMessage = async () => {
        if (!client || !interaction || !currentModel) {
            return
        }

        if (messages.length === 0) {
            const input = generateInputMessage(inputMessage, files)
            const taskName = generateTaskName(input)

            const collection = await client.store.collections.create({
                name: taskName,
                dynamic: false,
            })

            const payload = {
                type: "conversation",
                data: {
                    task: input,
                    collection: collection.id,
                },
                interactive: true,
                interaction: interaction.endpoint,
                environment: currentModel.environment,
                model: currentModel.id,
                tools: [],
                tags: [accessType, `collection:${collection.id}`],
                visibility: accessType === "private" ? "private" : "project",
            } as AsyncConversationExecutionPayload

            client.interactions
                .executeAsync(payload)
                .then(async (run) => {
                    setRun(run)

                    // Create a corresponding execution run with proper tagging for history tracking
                    await client.runs.create({
                        interaction: interaction.id,
                        data: {
                            task: input,
                            collection: collection.id,
                        },
                        config: {
                            model: currentModel.id,
                            environment: currentModel.environment,
                        },
                        tags: [accessType, `collection:${collection.id}`, `wf-run:${run.runId}`],
                    }).then(() => {
                        console.log("Successfully created tracking run for history")
                    }).catch((err) => {
                        console.warn("Failed to create tracking run:", err)
                    })

                    client.store.collections.update(collection.id, {
                        properties: {
                            status: "in_progress",
                        },
                    })

                    location.hash = `#${run.runId + "__" + run.workflowId}`
                    updateDocumentTitle(taskName)
                    setMessages((prevMessages) => [...prevMessages, initialMessage(input, run.runId)])

                    setInputMessage("")
                    setFiles([])
                    setIsCompleted(false)
                    setAgentModeValue(accessType)
                })
                .catch((err) => {
                    toast.error("Failed to start conversation", {
                        description: err.message,
                        duration: 5000,
                    })
                    client.store.collections.delete(collection.id)
                })
        } else {
            if (!run) {
                return
            }

            const message = inputMessage.trim()
            if (!message || isSending) {
                return
            }

            setIsSending(true)
            setInputMessage("")
            setIsCompleted(false)

            const payload = {
                message: generateInputMessage(message, files),
            } as UserInputSignal

            setFiles([])

            client.store.workflows
                .sendSignal(run.workflowId, run.runId, "UserInput", payload)
                .then(() => {
                    setIsCompleted(true)
                })
                .catch((err) => {
                    toast.error("Failed to send message", {
                        description: err.message,
                        duration: 5000,
                    })
                })
                .finally(() => {
                    setIsSending(false)
                })
        }
    }

    const handleChatMessage = async () => {
        if (inputMessage.trim() === "" || !client || !chat || !currentModel) {
            return
        }

        setLoadData(false)

        let tmpChatId = chatId
        if (!tmpChatId) {
            tmpChatId = crypto.randomUUID()
            location.hash = `#${tmpChatId}`
            setChatId(tmpChatId)
        }

        const input = generateInputMessage(inputMessage, files)

        const msg = generateMessage(input, tmpChatId, AgentMessageType.QUESTION)
        const chatMessage = generateChatMessage(chatMessages.length, input, tmpChatId, ChatMessageRoleType.USER)
        const documents = generateFilesList(files)

        setInputMessage("")
        setFiles([])

        setMessages((prevMessages) => [...prevMessages, msg])
        setChatMessages((prevMessages) => [...prevMessages, chatMessage])
        setIsCompleted(false)

        let tmpCollection = collection
        if (!tmpCollection) {
            const taskName = generateTaskName(input)
            updateDocumentTitle(taskName)
            tmpCollection = await client.store.collections.create({
                name: taskName,
                dynamic: false,
            })
            setCollection(tmpCollection)
        }

        client.interactions
            .execute(chat.id, {
                config: {
                    model: currentModel.id,
                    environment: currentModel.environment,
                },
                data: {
                    documents,
                    chat: [...chatMessages, chatMessage],
                },
                tags: [`chat:${tmpChatId}`, `collection:${tmpCollection.id}`],
            })
            .then((response) => {
                setMessages((prevMessages) => [...prevMessages, generateMessage(response.result, tmpChatId, AgentMessageType.ANSWER)])
                setChatMessages((prevMessages) => [...prevMessages, generateChatMessage(chatMessages.length + 1, response.result, tmpChatId, ChatMessageRoleType.SYSTEM)])

                if (!agentModeValue) {
                    setAgentModeValue("private")
                }
            })
            .finally(() => {
                setIsCompleted(true)
            })
    }

    const handleLoadChatHistory = async () => {
        if (!client || !chatId || !loadData) {
            return
        }

        setLoadModel(false)
        const chatQuery = { tags: { $in: [`chat:${chatId}`] } }
        const chatRuns = await client.runs.find({ query: chatQuery })
        if (chatRuns && chatRuns.length > 0) {
            setChatMessages([])
            setMessages([])
            setAgentModeValue("private")

            const tmpMessages: AgentMessage[] = []
            const tmpChatMessages: Message[] = []

            let messageIndex = 0
            chatRuns.forEach((run) => {
                const input = generateInputMessage(run.parameters.chat[messageIndex].content, [])
                const created_at = new Date(run.created_at)
                tmpMessages.push(generateMessage(input, chatId, AgentMessageType.QUESTION, created_at.getTime()))
                tmpChatMessages.push(generateChatMessage(chatMessages.length, input, chatId, ChatMessageRoleType.USER, created_at))
                messageIndex++

                const updated_at = new Date(run.updated_at)
                tmpMessages.push(generateMessage(run.result, chatId, AgentMessageType.ANSWER, updated_at.getTime()))
                tmpChatMessages.push(generateChatMessage(messageIndex, run.result, chatId, ChatMessageRoleType.SYSTEM, updated_at))
                messageIndex++
            })

            setModel(chatRuns[chatRuns.length - 1].config?.model)
            localStorage.setItem(AGENT_MODE_STATE, "basic")
            setAgentMode(false)
            setMessages(tmpMessages)
            setChatMessages(tmpChatMessages)
            setIsCompleted(true)

            const collectionId = extractCollectionIdFromExecution(chatRuns[0] as ExecutionRunRef)
            if (collectionId) {
                const collectionData = await client.store.collections.get(collectionId).catch((err) => {
                    console.warn(`Failed to load collection ${collectionId}`, err)
                })
                if (collectionData) {
                    setCollection(collectionData)
                    updateDocumentTitle(collectionData.name)
                }
            }

            return
        }

        const serviceQuery = { tags: { $in: [`service:${chatId}`] } }
        const serviceRuns = await client.runs.find({ query: serviceQuery })
        if (serviceRuns && serviceRuns.length === 1) {
            const int = await client.interactions.retrieve((serviceRuns[0] as any).interaction)
            setInteractionRef(int as InteractionRef)
            setSearchInteraction(false)
            setAgentInteraction(false)
            setLoadServiceData(serviceRuns[0] as ExecutionRun)
            return
        }

        navigate("/404")
    }

    const handleInteractionSelected = (interaction: InteractionRef) => {
        setInteractionRef(interaction)
        const agentInteraction = interaction.tags?.includes("agent")
        setAgentInteraction(agentInteraction)
        setSearchInteraction(false)
        setBrowseInteraction(false)
    }

    const handleOnBackToSearch = () => {
        if (location.hash) {
            window.history.pushState(null, "", window.location.pathname)
        }

        setSearchInteraction(true)
        setBrowseInteraction(false)
        setAgentInteraction(false)
        setInteractionRef(undefined)
        setRun(undefined)
        setRunDetails(undefined)
        setChatId(undefined)
        setLoadData(true)
        setLoadServiceData(undefined)
        setAgentMode(localStorage.getItem(AGENT_MODE_STATE) !== "basic")
        setAgentModeValue(undefined)
        setAccessType("private")
    }

    const retrieveAndUpdateCollection = async (collectionId: string) => {
        client.store.collections
            .get(collectionId)
            .then((collectionData) => {
                if (collectionData) {
                    setCollection(collectionData)
                    updateDocumentTitle(collectionData.name)
                    const properties = collectionData.properties || {}
                    const status = properties.status

                    if (status === "to_review") {
                        properties.status = "reviewed"
                        client.store.collections.update(collectionId, { properties })
                    }
                }
            })
            .catch((err) => {
                console.warn(`Failed to load collection ${collectionId}`, err)
            })
    }

    useEffect(() => {
        if (!client || !agent) {
            setInteraction(undefined)
            return
        }

        if (loadModel && loadData) {
            setModel(undefined)
        }
        client.interactions
            .retrieve(agent.id)
            .then((response) => {
                setInteraction(response as Interaction)
                if (loadModel && loadData) {
                    setModel(response.model)
                }
            })
            .catch((error) => {
                console.warn("Error fetching interaction:", error)
            })
    }, [agent])

    useEffect(() => {
        handleLoadChatHistory()
    }, [chatId])

    useEffect(() => {
        if (!client || !run) {
            return
        }

        setLoadData(false)
        if (!agentModeValue && !runDetails) {
            const query = { tags: { $in: [`wf-run:${run.runId}`] } }
            client.runs.find({ query }).then(async (results) => {
                if (results && results[0]) {
                    const tmpInt = results[0].interaction as unknown as string
                    const taskName = tmpInt ? (await client.interactions.retrieve(tmpInt))?.name || "Undefined Task Name" : "Undefined Task Name"
                    setMessages((prev_messages) => {
                        const message = results[0].parameters?.task ? initialMessage(results[0].parameters.task, run.runId, new Date(results[0].created_at).getTime()) : initialDetailedMessage(`Can you help with ${taskName}`, results[0].parameters, run.runId, new Date(results[0].created_at).getTime())
                        insertMessageInTimeline(prev_messages, message)
                        return [...prev_messages]
                    })
                    setAgentModeValue(results[0].tags?.includes("private") ? "private" : "shared")
                    setModel(results[0].modelId)
                }
            })
        }

        if (!runDetails) {
            client.workflows
                .getRunDetails(run.runId, run.workflowId)
                .then((details) => {
                    setRunDetails(details)
                    const collectionId = extractCollectionId(details)
                    collectionId && retrieveAndUpdateCollection(collectionId)
                })
                .catch(() => {
                    location.hash = ""
                    handleNewChat()
                })
        }

        client.store.workflows.streamMessages(run.workflowId, run.runId, (message: any) => {
            if (message.message) {
                setMessages((prev_messages) => {
                    if (!prev_messages.find((m) => m.timestamp === message.timestamp)) {
                        insertMessageInTimeline(prev_messages, message)
                        return [...prev_messages]
                    } else {
                        return prev_messages
                    }
                })
            }
        })

        return () => {
            setMessages([])
        }
    }, [run, client.store.workflows])

    useEffect(() => {
        if (!chatId) {
            setIsCompleted(isInProgress(messages))
        }
    }, [messages])

    useEffect(() => {
        messages.forEach((message) => {
            if (message.type === AgentMessageType.PLAN) {
                try {
                    let newPlanDetails: Plan | null = null
                    if (message.details && typeof message.details === "object") {
                        if (message.details.plan && Array.isArray(message.details.plan)) {
                            newPlanDetails = { plan: message.details.plan } as Plan
                        }
                    }

                    if (newPlanDetails) {
                        const timestamp = typeof message.timestamp === "number" ? message.timestamp : new Date(message.timestamp).getTime()

                        const existingPlanIndex = plans.findIndex((p) => p.timestamp === timestamp)

                        if (existingPlanIndex === -1 && newPlanDetails) {
                            const newPlan = {
                                plan: newPlanDetails,
                                timestamp,
                            }

                            setPlans((prev) => {
                                return [newPlan, ...prev]
                            })
                            setActivePlanIndex(0)

                            const newWorkstreamStatus = new Map<string, "pending" | "in_progress" | "completed" | "skipped">()

                            newWorkstreamStatus.set("main", "in_progress")

                            if (Array.isArray(newPlanDetails.plan)) {
                                newPlanDetails.plan.forEach((task) => {
                                    if (task && typeof task === "object" && task.id) {
                                        const taskId = task.id.toString()
                                        newWorkstreamStatus.set(taskId, task.status || "pending")
                                    }
                                })
                            }

                            setWorkstreamStatusMap((prev) => {
                                const newMap = new Map(prev)
                                newMap.set(timestamp, newWorkstreamStatus)
                                return newMap
                            })
                        }
                    }
                } catch (error) {
                    toast.error("Failed to parse plan from message", {
                        description: error instanceof Error ? error.message : "Unknown error occurred",
                        duration: 5000,
                    })
                }
            }

            if ((message.type === AgentMessageType.ANSWER || message.type === AgentMessageType.UPDATE) && message.message) {
                const msg = typeof message.message === "string" ? message.message : JSON.stringify(message.message)
                const files = msg.match(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi)
                for (const file of files || []) {
                    const fileId = file.match(/\(store:([a-f\d]{24})\)/)?.[1]
                    if (fileId && !createdFiles.includes(fileId)) {
                        setCreatedFiles((prev) => [...prev, fileId])
                    }
                }
            }
        })

        collection && retrieveAndUpdateCollection(collection.id)
    }, [messages, plans, activePlanIndex])

    useEffect(() => {
        if (defaultAgent || chat) {
            return
        }
        search.search()
    }, [])

    const extractFilesFromMessages = async () => {
        const fileIds = createdFiles.filter((fileId) => !fileMessages.find((f) => f.id === fileId) && !filesToPass.includes(fileId))
        Promise.all(
            Array.from(fileIds).map((fileId) =>
                client.objects
                    .retrieve(fileId)
                    .then((file) => {
                        const fileMessage: FileMessage = {
                            id: file.id,
                            name: file.properties?.title || file.name || file.id,
                        }
                        setFileMessages((prev) => {
                            const exists = prev.find((f) => f.id === file.id)
                            return exists ? prev : [...prev, fileMessage]
                        })
                    })
                    .catch(() => {
                        setFilesToPass((prev) => [...prev, fileId])
                    })
            )
        )
    }

    useEffect(() => {
        extractFilesFromMessages()
    }, [createdFiles])

    useEffect(() => {
        if (defaultAgent && !chat) {
            onAgentModeChange(true)
        }
        if (!defaultAgent && chat) {
            onAgentModeChange(false)
        }
    }, [defaultAgent, chat])

    const getActivePlan = useMemo(() => {
        const currentPlanData = plans[activePlanIndex] || { plan: { plan: [] }, timestamp: 0 }
        const currentWorkstreamStatus = workstreamStatusMap.get(currentPlanData.timestamp) || new Map()
        return {
            plan: currentPlanData.plan,
            workstreamStatus: currentWorkstreamStatus,
        }
    }, [plans, activePlanIndex, workstreamStatusMap])

    return (
        <>
            {searchInteraction && <Header agentMode={agentModeValue} runId={run?.runId} chatId={chatId} collection={collection} viewMode={viewMode} onViewModeChange={setViewMode} onCopyRunId={handleCopyRunId} onCopyChatId={handleCopyChatId} onNewChat={handleNewChat} onNameChange={handleOnNameChange} onShare={handleOnShare} />}
            <div className={`flex flex-row items-center justify-center gap-2 transition-all duration-500 ease-in-out overflow-hidden ${searchInteraction && !browseInteraction ? "h-full" : "h-0"}`}>
                {plans.length > 0 || fileMessages.length || (collection as any)?.properties?.metrics ? (
                    <ResizablePanel
                        leftPanel={<PlanPanel files={fileMessages} metrics={(collection as any)?.properties?.metrics || {}} plan={getActivePlan.plan} workstreamStatus={getActivePlan.workstreamStatus} />}
                        rightPanel={
                            <div className="w-full h-full flex flex-col max-w-[60vw] mx-auto items-center justify-center px-4 pt-8 pb-4 gap-4">
                                {messages.length !== 0 && <AllMessagesMixed messages={messages} bottomRef={bottomRef as React.RefObject<HTMLDivElement>} viewMode={viewMode} />}
                                {messages.length !== 0 && !isCompleted && <LatestUpdateMessagePanel messages={messages} />}
                                {(!run || runDetails?.status === "RUNNING") && (
                                    <div className="flex flex-col items-center w-full bg-white border border-gray-300 rounded-3xl p-4 gap-2">
                                        {((agentMode && defaultAgent === agent) || run || !agentMode) && (
                                            <>
                                                <div className="flex items-center w-full items-end gap-1">
                                                    <Textarea ref={textareaRef} value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={handleOnKeyDown} placeholder="Ask Vertesia anything..." className="max-h-[50vh] flex-1 border-none focus-visible:ring-0 focus:ring-0 focus:outline-none outline-none rounded-none shadow-none" />
                                                </div>
                                                <div className="flex items-center justify-between w-full gap-2">
                                                    <div className="flex gap-2">
                                                        <AgentModelSelection disabled={!!agentMode} model={model} onChange={setCurrentModel} />
                                                        <AgentModeBadge state={agentMode} onChange={onAgentModeChange} disabled={!!run || !!chatId || (agentMode && !chat) || (!agentMode && !defaultAgent)} />
                                                        {!run && !chatId && <AgentAccessModeBadge disabled={!agentMode || !!run || !!chatId} value={accessType} onChange={onAccessTypeChange} />}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FilesSelectionPanel onChange={handleFilesUpdate} />
                                                        <div>
                                                            <ActionTooltipButton disabled={!inputMessage} icon={<ArrowUp />} variant="icon" size="icon" tooltip="Send message" onClick={handleSendMessage} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {files.length > 0 && (
                                    <div className="w-full">
                                        <div className="flex-1 overflow-hidden">
                                            <div className="flex flex-row items-center gap-2 overflow-x-auto hide-scrollbar">
                                                {files.map((file) => (
                                                    <FileSelectedButton key={file.id} document={file} onRemove={handleFilesRemove} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        }
                        defaultLeftWidth={450}
                        minLeftWidth={250}
                        maxLeftWidth={600}
                    />
                ) : (
                    <div className="w-full h-full flex flex-col max-w-[60vw] mx-auto items-center justify-around px-4 pt-8 pb-4 gap-4">
                        {messages.length !== 0 && <AllMessagesMixed messages={messages} bottomRef={bottomRef as React.RefObject<HTMLDivElement>} viewMode={viewMode} />}
                        {messages.length !== 0 && !isCompleted && <LatestUpdateMessagePanel messages={messages} />}
                        {(!run || runDetails?.status === "RUNNING") && (
                            <div className="flex flex-col items-start w-full gap-2">
                                {messages.length === 0 && (
                                    <div className="flex flex-col items-center w-full mb-4">
                                        <p className="text-center text-2xl font-medium tracking-tight">
                                            Hey <span className="text-fuchsia-500 hover:cursor-pointer hover:underline">{username}</span>, how can I help you ?
                                        </p>
                                    </div>
                                )}
                                <div className="flex flex-col items-center w-full bg-white border border-gray-300 rounded-3xl p-4 gap-2">
                                    {!defaultAgent && !chat ? (
                                        <>
                                            <span className="font-medium text-lg">You don't have any default Agent or Chat</span>
                                            <span className="font-light text-lg">Explore the services that are available</span>
                                        </>
                                    ) : (
                                        ((agentMode && defaultAgent === agent) || run || !agentMode) && (
                                            <>
                                                <div className="flex items-center w-full items-end gap-1">
                                                    <Textarea ref={textareaRef} value={inputMessage} onChange={(e) => setInputMessage(e.target.value)} onKeyDown={handleOnKeyDown} placeholder="Ask Vertesia anything..." className="max-h-[50vh] flex-1 border-none focus-visible:ring-0 focus:ring-0 focus:outline-none outline-none rounded-none shadow-none" />
                                                </div>
                                                <div className="flex items-center justify-between w-full gap-2">
                                                    <div className="flex gap-2">
                                                        <AgentModelSelection disabled={!!agentMode} model={model} onChange={setCurrentModel} />
                                                        <AgentModeBadge state={agentMode} onChange={onAgentModeChange} disabled={!!run || !!chatId || (agentMode && !chat) || (!agentMode && !defaultAgent)} />
                                                        {!run && !chatId && <AgentAccessModeBadge disabled={!agentMode || !!run || !!chatId} value={accessType} onChange={onAccessTypeChange} />}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <FilesSelectionPanel onChange={handleFilesUpdate} />
                                                        <div>
                                                            <ActionTooltipButton disabled={!inputMessage} icon={<ArrowUp />} variant="icon" size="icon" tooltip="Send message" onClick={handleSendMessage} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    )}
                                </div>
                                {files.length > 0 && (
                                    <div className="w-full">
                                        <div className="flex-1 overflow-hidden">
                                            <div className="flex flex-row items-center gap-2 overflow-x-auto hide-scrollbar">
                                                {files.map((file) => (
                                                    <FileSelectedButton key={file.id} document={file} onRemove={handleFilesRemove} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {messages.length === 0 && !browseInteraction && searchInteraction && (
                            <Button variant="ghost" className="text-base text-muted-foreground" onClick={() => setBrowseInteraction(true)}>
                                Explore available services <ChevronsDown />
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {messages.length === 0 && (
                <div className={`flex flex-row items-center justify-center gap-2 transition-all duration-500 ease-in-out overflow-hidden ${browseInteraction ? "h-full" : "h-0"}`}>
                    <div className="w-full h-full flex flex-col max-w-4xl mx-auto items-center justify-center px-4 pt-2 pb-4 gap-4">
                        <Button variant="ghost" className="z-99 text-base text-muted-foreground" onClick={() => setBrowseInteraction(false)}>
                            Start a new Conversation <ChevronsUp />
                        </Button>
                        <AvailableServicesPanel onInteractionSelected={handleInteractionSelected} />
                    </div>
                </div>
            )}
            <div className={`absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden ${!searchInteraction && !agentInteraction ? "h-full" : "h-0"}`}>
                <ServicePanel interactionRef={interactionRef} onBackToSearch={handleOnBackToSearch} payload={loadServiceData} reset={!searchInteraction && !agentInteraction} />
            </div>
            <div className={`absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden ${!searchInteraction && agentInteraction ? "h-full" : "h-0"}`}>
                <AgentPanel interactionRef={interactionRef} onBackToSearch={handleOnBackToSearch} onPayloadReady={handleOnPayloadReady} />
            </div>
        </>
    )
}

function decodeWorkflowRun(hash: string) {
    if (!hash) {
        return undefined
    }
    hash = hash.substring(1)
    const index = hash.indexOf("__")
    if (index > 0) {
        const runId = hash.substring(0, index)
        const workflowId = hash.substring(index + 2)
        if (runId && workflowId) {
            return { runId, workflowId }
        }
    }
    return undefined
}

function decodeChatId(hash: string) {
    if (!hash) {
        return undefined
    }

    hash = hash.substring(1)
    const index = hash.indexOf("__")
    return index === -1 ? hash : undefined
}

function generateInputMessage(input: string, files: ContentObjectItem[]) {
    if (!files.length) {
        return input
    }

    for (const file of files) {
        const title = file.properties?.title || file.name || "Object"
        input += `\n\n[${title}](store:${file.id})`
    }

    return input
}

function generateFilesList(input: ContentObjectItem[]) {
    return input.map((file) => `store:${file.id}`)
}

function generateTaskName(input: string | Interaction) {
    const date = new Date()

    return typeof input === "object" ? `${input.name} - ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : createTaskName(input)
}

function createTaskName(input: string, maxLength = 50) {
    if (!input) {
        return ""
    }

    let trimmed = input.trim()

    const firstLine = trimmed.split("\n")[0]

    const questionIndex = firstLine.indexOf("?")
    const exclamationIndex = firstLine.indexOf("!")

    let stopIndex = -1
    if (questionIndex !== -1 && exclamationIndex !== -1) {
        stopIndex = Math.min(questionIndex, exclamationIndex)
    } else if (questionIndex !== -1) {
        stopIndex = questionIndex
    } else if (exclamationIndex !== -1) {
        stopIndex = exclamationIndex
    }

    trimmed = stopIndex !== -1 ? firstLine.substring(0, stopIndex + 1) : firstLine

    if (trimmed.length <= maxLength) {
        return trimmed
    }

    const truncated = trimmed.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(" ")

    return lastSpaceIndex === -1 ? truncated : truncated.substring(0, lastSpaceIndex)
}
